import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { Globals } from '../../ressources/variables/globals';

export interface LaravelUser {
  id: number;
  username: string;
  nickname?: string | null;
  email: string;
  email_verified_at?: string;
  created_at?: string;
  updated_at?: string;
  team_id?: number;
  address?: string;
  city?: string;
  birth_date?: string;
  profile_picture_url?: string;
  role?: string;
}

@Injectable({
  providedIn: 'root'
})
export class LaravelAuthService {
  globals = Globals;

  constructor(private http: HttpClient) {}

  /**
   * Récupère le cookie CSRF nécessaire pour l'authentification Sanctum
   * Cette méthode doit être appelée AVANT toute tentative de login
   */
  getCsrfCookie(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.http.get(this.globals.websiteRacine + '/sanctum/csrf-cookie', {
        withCredentials: true,
        headers: new HttpHeaders({
          'Accept': 'application/json'
        })
      }).toPromise()
        .then(() => resolve())
        .catch(err => reject(err));
    });
  }

  /**
   * Récupère le token XSRF depuis les cookies
   */
  private getXsrfToken(): string {
    const name = 'XSRF-TOKEN=';
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookieArray = decodedCookie.split(';');

    for (let cookie of cookieArray) {
      cookie = cookie.trim();
      if (cookie.indexOf(name) === 0) {
        return cookie.substring(name.length, cookie.length);
      }
    }
    return '';
  }

  /**
   * Connexion via Laravel Sanctum
   * @param email Email de l'utilisateur
   * @param password Mot de passe
   */
  login(email: string, password: string): Promise<LaravelUser> {
    return new Promise((resolve, reject) => {
      // D'abord, obtenir le cookie CSRF
      this.getCsrfCookie()
        .then(() => {
          const headers = new HttpHeaders({
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'X-XSRF-TOKEN': this.getXsrfToken()
          });

          this.http.post(
            this.globals.websiteRacine + '/auth/login',
            { email, password },
            { headers, withCredentials: true }
          ).toPromise()
            .then(() => {
              // Après le login réussi, récupérer les données utilisateur via /sanctum/user
              this.getCurrentUser()
                .then(user => {
                  resolve(user);
                })
                .catch(err => {
                  reject('Login réussi mais impossible de récupérer les données utilisateur');
                });
            })
            .catch(err => {
              const errorMessage = err.error?.message || err.error?.error || 'Erreur de connexion';
              reject(errorMessage);
            });
        })
        .catch(err => reject('Impossible d\'obtenir le token CSRF'));
    });
  }

  /**
   * Déconnexion via Laravel Sanctum
   */
  logout(): Promise<void> {
    return new Promise((resolve, reject) => {
      const headers = new HttpHeaders({
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-XSRF-TOKEN': this.getXsrfToken()
      });

      this.http.post(
        this.globals.websiteRacine + '/auth/logout',
        {},
        { headers, withCredentials: true }
      ).toPromise()
        .then(() => {
          this.deleteSession();
          resolve();
        })
        .catch(err => {
          // On supprime la session locale même en cas d'erreur
          this.deleteSession();
          reject(err);
        });
    });
  }

  /**
   * Récupère l'utilisateur actuellement connecté
   * Utile pour vérifier si la session est encore valide au chargement de l'app
   */
  getCurrentUser(): Promise<LaravelUser> {
    return new Promise((resolve, reject) => {
      this.http.get<LaravelUser>(this.globals.websiteRacine + '/sanctum/user', {
        withCredentials: true,
        headers: new HttpHeaders({
          'Accept': 'application/json'
        })
      }).toPromise()
        .then(user => {
          if (user && user.id) {
            this.saveSession(user);
            resolve(user);
          } else {
            this.deleteSession();
            reject('Utilisateur non connecté');
          }
        })
        .catch(err => {
          this.deleteSession();
          reject(err);
        });
    });
  }

  /**
   * Vérifie si l'utilisateur a une session active
   * Cette méthode tente de charger l'utilisateur courant
   */
  checkSession(): Promise<LaravelUser> {
    return this.getCurrentUser();
  }

  /**
   * Sauvegarde les informations de session en localStorage
   */
  private saveSession(user: LaravelUser): void {
    localStorage.setItem('laravel_user_id', String(user.id));
    localStorage.setItem('laravel_user_name', user.username);
    if (user.nickname) {
      localStorage.setItem('laravel_user_nickname', user.nickname);
    } else {
      localStorage.removeItem('laravel_user_nickname');
    }
    localStorage.setItem('laravel_user_email', user.email);
  }

  /**
   * Récupère les informations de session depuis localStorage
   */
  getStoredSession(): { id: number; name: string; nickname: string | null; email: string } | null {
    const id = localStorage.getItem('laravel_user_id');
    const name = localStorage.getItem('laravel_user_name');
    const nickname = localStorage.getItem('laravel_user_nickname');
    const email = localStorage.getItem('laravel_user_email');

    if (id && name && email) {
      return {
        id: parseInt(id, 10),
        name,
        nickname,
        email
      };
    }
    return null;
  }

  /**
   * Supprime les informations de session
   */
  deleteSession(): void {
    localStorage.removeItem('laravel_user_id');
    localStorage.removeItem('laravel_user_name');
    localStorage.removeItem('laravel_user_nickname');
    localStorage.removeItem('laravel_user_email');
  }

  /**
   * Vérifie si un utilisateur est stocké en local
   */
  hasStoredSession(): boolean {
    return localStorage.getItem('laravel_user_id') !== null;
  }
}
